/*
  Workspace
  The main workspace area of the form builder
*/

import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { filter } from 'rxjs';
import { ID } from '@datorama/akita';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { PresenceChannel } from 'pusher-js';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';

import { ConfirmationModalComponent, ConfirmationModalData } from '@component/confirmation-modal';
import { DocumentFieldDeletionStatus } from '@app/shared/services/document.model';
import { DocumentService, EditorState, FormService as FormLegacyService } from '@app/shared/services';
import { DocumentViewerComponent } from '@app/shared/components/document-viewer/document-viewer.component';
import { FormRepository, FormSection, FormService, PageObjectType, SelectableType } from '@state/form';
import { PusherPresenceService } from '@state/pusher-presence/pusher-presence.service';
import { noIndent, toSentenceCase } from '@helpers/string';
import { UserQuery } from '@state/user';
import { ZendeskService } from '@state/zendesk';

import { FormInfoModalComponent } from './form-info-modal';

export enum WorkspaceType {
  Document = 'document',
  Form = 'form',
  Rtd = 'rtd'
}

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-workspace',
  styleUrls: ['./workspace.component.scss'],
  templateUrl: './workspace.component.html'
})
export class WorkspaceComponent implements OnInit, OnDestroy {
  @Input() workspaceType: WorkspaceType;

  @Output() setWorkspaceType = new EventEmitter<WorkspaceType>();

  activeSessions$ = this.formRepository.activeSessions$;
  formUpdatedAt$ = this.formRepository.updatedAt$;
  membersOnline$ = this.formRepository.membersOnline$;
  user$ = this.userQuery.select('user');

  advancedRichTextEditor = false;
  channel: PresenceChannel;
  channelString: string;
  currentWorkspace: TemplateRef<any>;
  DocumentFieldDeletionStatus = DocumentFieldDeletionStatus;
  pageObjectType = PageObjectType;
  presenceActive = false;

  readonly WorkspaceType = WorkspaceType;

  // Available templates
  @ViewChild('documentWorkspace', { static: true }) documentWorkspace: TemplateRef<any>;
  @ViewChild('formWorkspace', { static: true }) formWorkspace: TemplateRef<any>;
  @ViewChild('rtdWorkspace', { static: true }) rtdWorkspace: TemplateRef<any>;
  @ViewChild('docViewer') docViewer: DocumentViewerComponent;

  @HostListener('click', ['$event'])
  onClick(event: any): void {
    let element = event.target;
    let selectableComponent = element.closest('.component-selectable');

    if (selectableComponent?.dataset) {
      const id = selectableComponent.dataset.id;
      const { selectableType }: { selectableType: SelectableType } = selectableComponent.dataset;
      if (selectableType === SelectableType.Mapper) return;

      // only change the selected item if a section/element/form is selected. If click
      // event is triggered anywhere else (e.g. sidebar or toolbar) then don't deselect
      if ([SelectableType.Section, SelectableType.Element].includes(selectableType)) {
        let methodType = toSentenceCase(selectableType);
        let method = `queryForm${methodType}ById`;
        this.editorState.selectedItem = this.formLegacyService[method](id);
      } else if (selectableType === SelectableType.Rtd) {
        this.editorState.selectedItem = this.formLegacyService.form;
      } else if (selectableType === SelectableType.Form) {
        this.editorState.selectedItem = this.formLegacyService.form;
      } else if (selectableType === SelectableType.Document) {
        this.editorState.selectedItem = this.documentService.document;
        this.docViewer.clearMapperSelection();
      }
      this.editorState.selectedItem.selectableType = selectableType;
    }
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    let element = event.target as HTMLElement;
    let draggableField: HTMLElement = element.closest('.draggable-field');
    if (draggableField) {
      let dragElement = document.createElement('div');
      dragElement.classList.add('drag-element');
      dragElement.classList.add(draggableField.dataset.pageObjectType);
      dragElement.style.top = `${event.clientY}px`;
      dragElement.style.left = `${event.clientX}px`;
      this.el.nativeElement.appendChild(dragElement);
    }
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    let dragElement: HTMLDivElement = document.querySelector('.drag-element');

    if (dragElement) {
      dragElement.style.top = `${event.clientY}px`;
      dragElement.style.left = `${event.clientX}px`;
    }
  }

  @HostListener('mouseup', ['$event'])
  onMouseUp(): void {
    let dragElement: HTMLDivElement = document.querySelector('.drag-element');
    if (dragElement) dragElement.remove();
  }

  constructor(
    private el: ElementRef,
    private destroyRef: DestroyRef,
    private location: Location,
    private ngZone: NgZone,
    private titleService: Title,
    private userQuery: UserQuery,
    public dialog: MatDialog,
    public documentService: DocumentService,
    public editorState: EditorState,
    public formLegacyService: FormLegacyService,
    public formRepository: FormRepository,
    public formService: FormService,
    public pusherPresenceService: PusherPresenceService,
    public zendeskService: ZendeskService
  ) {}

  ngOnInit() {
    this.formRepository.formId$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (id: ID) => {
          if (id) {
            this.presenceActive = true;
            this.channelString = `form/${id}`;
            this.pusherPresenceService.createPresenceChannel(this.channelString, {
              added: (data) => {
                this.formService.addMember(data);
              },
              removed: (data) => {
                this.formService.removeMember(data.id);
              },
              success: (data) => {
                this.formService.setMembersOnline(data.members);
              }
            });
          }
        }
      });

    this.setWorkspace(this.workspaceType);
    if (this.workspaceType === WorkspaceType.Rtd) this.setRichTextEditorView('basic');

    this.ngZone.runOutsideAngular(() => {
      this.zendeskService.toggleZendeskWidgetDisplay();
    });
  }

  ngOnDestroy() {
    this.zendeskService.toggleZendeskWidgetDisplay();

    if (this.channelString) {
      this.pusherPresenceService.destroyPresenceChannel(this.channelString);
    }
  }

  displayExitConfirmationModal(): void {
    if (!this.isDeleting()) return;
    let data: ConfirmationModalData = {
      message: `Please hold, processing deleting elements`,
      positiveButtonLabel: 'OK',
      title: 'Processing requests'
    };
    this.dialog.open(ConfirmationModalComponent, { data });
  }

  fieldButtonSelected(type: any): void {
    this.documentService.fieldButtonSelected = type;
  }

  isDeleting(): boolean {
    let [currentDeletionStatus] = this.documentService.deletionState;
    return currentDeletionStatus === DocumentFieldDeletionStatus.Deleting;
  }

  removeElement(event: any): void {
    let data: ConfirmationModalData = {
      destructive: true,
      message: noIndent(`
        Deleting this element cannot be undone.
      `),
      positiveButtonLabel: 'Delete Element',
      title: 'Are you sure you want to delete this element?'
    };

    this.dialog
      .open(ConfirmationModalComponent, { data })
      .afterClosed()
      .pipe(filter((confirmed: boolean) => confirmed))
      .subscribe({
        next: () => {
          this.formLegacyService.removeElement(event.section.id, event.element, {
            success: () => {
              if (this.formLegacyService.form) {
                this.editorState.selectedItem = this.formLegacyService.form;
                this.editorState.selectedItem.selectableType = SelectableType.Form;
              }
              if (this.formLegacyService.form.document_id) {
                this.documentService.getDocument$(this.documentService.document.id);
              }
            }
          });
        }
      });
  }

  removeSection(event: any): void {
    let formSection: FormSection = event.section;
    let data: ConfirmationModalData = {
      destructive: true,
      message: noIndent(`
        Deleting this section will also delete all it's elements and cannot be undone.
      `),
      positiveButtonLabel: 'Delete Section',
      title: 'Are you sure you want to delete this section?'
    };

    let dialogRef = this.dialog.open(ConfirmationModalComponent, { data });

    dialogRef.afterClosed().subscribe({
      next: (confirmed: boolean) => {
        if (confirmed) this.formLegacyService.removeSection(formSection);
      }
    });
  }

  scrollToElement(id: string): void {
    this.editorState.selectedItem = this.formLegacyService['queryFormSectionById'](id);
    this.editorState.selectedItem.selectableType = SelectableType.Section;

    const element = document.getElementById(id.toString());
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  setRichTextEditorView(view: string): void {
    if (this.isDeleting()) return;
    this.setWorkspace(WorkspaceType.Rtd);
    this.formLegacyService.searchCustomVariables('', null, null, 'fixed_text');
    this.advancedRichTextEditor = view === 'advanced';
  }

  setWorkspace(workspaceType: WorkspaceType): void {
    if (this.isDeleting()) return;
    if (workspaceType === WorkspaceType.Form) {
      this.editorState.selectedItem = this.formLegacyService.form;
      delete this.editorState.selectedItemPageObject;
      this.titleService.setTitle(`${this.formLegacyService.form.title} | Form Builder`);
      this.location.replaceState(`/builder/form/${this.formLegacyService.form.id}`);
    } else if (workspaceType === WorkspaceType.Document) {
      this.editorState.selectedItem = this.documentService.document;
      this.titleService.setTitle(`${this.documentService.document.title} | Form Builder`);
      this.location.replaceState(`/builder/document/${this.documentService.document.id}`);
    } else if (workspaceType === WorkspaceType.Rtd) {
      this.editorState.selectedItem = this.formLegacyService.form;
      this.titleService.setTitle(`${this.formLegacyService.form.title} | Form Builder`);
      this.location.replaceState(`/builder/rtd/${this.formLegacyService.form.id}`);
    }

    this.editorState.selectedItem.selectableType = this.workspaceType = workspaceType;

    this.currentWorkspace = this[`${this.workspaceType}Workspace`];

    this.setWorkspaceType.emit(this.workspaceType);
  }

  openInfoModal(): void {
    if (this.isDeleting()) {
      this.displayExitConfirmationModal();
      return;
    }
    this.dialog.open(FormInfoModalComponent, {
      data: {
        form: this.formLegacyService.form
      },
      panelClass: 'fl-import-sections-dialog'
    });
  }
}
